import React, { PureComponent } from 'react';
import { js as importJs, css as importCss } from '@clearscore/helpers.injectors';
import { hasComponent, loadComponent } from '@clearscore/helpers.cache';
import PropTypes from 'prop-types';

/**
 * Object used to store the current vertical Component and associated store,
 * which is then used maintain the store when navigating within the same vertical
 */
const currentStore = {};

/**
 * Component to lazy load a react component from the CDN, requires object which must contain
 * a component and id property
 */
class LazyLoadComponent extends PureComponent {
    state = {
        Component: null,
        js: false,
        css: false,
    };

    /**
     * When component is about to mount will go fetch the JS and related CSS
     */
    // eslint-disable-next-line camelcase
    UNSAFE_componentWillMount() {
        const {
            component: { name },
        } = this.props;

        if (hasComponent(name)) {
            const Component = loadComponent(name);
            this.setState({ Component, js: true, css: true });
        }
    }

    /**
     * Once component is mounted it checks to see if its actually loaded anything, if not starts the load process
     */
    componentDidMount() {
        const {
            component: { name, vertical, component },
        } = this.props;

        if (!hasComponent(name)) {
            const assetPath = vertical || component;
            this.loadJs(assetPath);
            this.loadCss(assetPath);
        }
    }

    componentDidUpdate(prevProps, prevState) {
        const { Component, css, js } = prevState;
        const { handleComplete } = prevProps;

        if (!Component || !css || !js) return null;

        return handleComplete();
    }

    /**
     * Loads the JS component into cache and injects into head updates state to reflect fetch
     */
    async loadJs(assetPath) {
        const {
            component: { name },
            handleError,
        } = this.props;

        try {
            await importJs(assetPath);
            const Component = loadComponent(name);
            this.setState({ Component, js: true });
        } catch (e) {
            handleError(e);
        }
    }

    /**
     * Loads related CSS file into the <head>
     */
    async loadCss(assetPath) {
        const {
            component: { name },
            handleError,
        } = this.props;

        const cssSrc = assetPath.replace('index.js', `${name}.css`);

        try {
            await importCss(cssSrc);
            this.setState({ css: true });
        } catch (e) {
            handleError(e);
        }
    }

    /**
     * Renders component
     * @return {React}
     */
    render() {
        const { Component, css, js } = this.state;
        const { handleComplete, component } = this.props;

        if (!Component || !css || !js) return null;

        handleComplete();
        return (
            <Component
                {...component.parentProps}
                {...component.routeProps}
                match={component.computedMatch}
                currentStore={currentStore}
                routeConfig={component}
            />
        );
    }
}

LazyLoadComponent.propTypes = {
    component: PropTypes.shape({
        routeProps: PropTypes.object.isRequired,
        computedMatch: PropTypes.object.isRequired,
        parentProps: PropTypes.object.isRequired,
        name: PropTypes.string.isRequired,
        vertical: PropTypes.string,
        component: PropTypes.string,
    }),
    handleComplete: PropTypes.func,
    handleError: PropTypes.func,
};

LazyLoadComponent.defaultProps = {
    component: null,
    handleError: () => {},
    handleComplete: () => {},
};

export default LazyLoadComponent;
